import React, { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useToast } from '../context/ToastContext';
import { authApi } from '../services/api';

const ResetPassword = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { addToast } = useToast();
    const [formData, setFormData] = useState({
        password: '',
        confirmPassword: ''
    });
    const [errors, setErrors] = useState({});
    const [isLoading, setIsLoading] = useState(false);

    // Extract email and token from URL query params
    const email = searchParams.get('email');
    const token = searchParams.get('token');

    const handleInputChange = (e) => {
        const { id, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [id]: value
        }));

        // Clear errors when typing
        setErrors(prev => ({
            ...prev,
            [id]: '',
            submit: ''
        }));

        // Check password match as typing
        if (id === 'password' || id === 'confirmPassword') {
            if (id === 'password' && formData.confirmPassword && value !== formData.confirmPassword) {
                setErrors(prev => ({
                    ...prev,
                    confirmPassword: 'Passwords do not match'
                }));
            } else if (id === 'confirmPassword' && value !== formData.password) {
                setErrors(prev => ({
                    ...prev,
                    confirmPassword: 'Passwords do not match'
                }));
            } else {
                setErrors(prev => ({
                    ...prev,
                    confirmPassword: ''
                }));
            }
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!email || !token) {
            addToast('Invalid reset link. Please request a new password reset.', 'error', 6000);
            navigate('/forgot-password');
            return;
        }

        if (formData.password.length < 6) {
            setErrors(prev => ({
                ...prev,
                password: 'Password must be at least 6 characters long'
            }));
            return;
        }

        if (formData.password !== formData.confirmPassword) {
            setErrors(prev => ({
                ...prev,
                confirmPassword: 'Passwords do not match'
            }));
            return;
        }

        try {
            setIsLoading(true);
            const response = await authApi.resetPassword({
                email,
                token,
                newPassword: formData.password
            });

            addToast('Password reset successfully! You can now log in.', 'success', 6000);
            setTimeout(() => {
                navigate('/login');
            }, 1500);
        } catch (error) {
            const errorMessage = error?.error || error?.message || 'Failed to reset password';
            addToast(errorMessage, 'error', 8000);
            setErrors(prev => ({
                ...prev,
                submit: errorMessage
            }));
        } finally {
            setIsLoading(false);
        }
    };

    if (!email || !token) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-black text-white" style={{ marginTop: '-96px' }}>
                <div className="bg-black p-8 rounded-lg shadow-md w-96 text-center">
                    <h2 className="text-2xl font-semibold mb-4 text-red-500">Invalid Reset Link</h2>
                    <p className="text-gray-300 mb-6">The password reset link is invalid or missing required parameters.</p>
                    <Link to="/forgot-password" className="text-lime-400 hover:underline">
                        Request New Password Reset Link
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="flex items-center justify-center min-h-screen bg-black text-white" style={{ marginTop: '-96px' }}>
            <div className="bg-black p-8 rounded-lg shadow-md w-96">
                <h2 className="text-2xl font-semibold mb-4 text-center">Reset Password</h2>
                
                {errors.submit && (
                    <div className="text-red-500 text-center mb-4">{errors.submit}</div>
                )}

                <form onSubmit={handleSubmit}>
                    <div className="mb-4">
                        <label className="block mb-2">New Password</label>
                        <input
                            type="password"
                            id="password"
                            value={formData.password}
                            onChange={handleInputChange}
                            className="w-full p-2 rounded border border-gray-300 text-black"
                            placeholder="Enter your new password"
                            required
                            disabled={isLoading}
                            minLength={6}
                            autoComplete="new-password"
                        />
                        {errors.password && (
                            <div className="text-red-500 text-sm mt-1">{errors.password}</div>
                        )}
                    </div>

                    <div className="mb-4">
                        <label className="block mb-2">Confirm Password</label>
                        <input
                            type="password"
                            id="confirmPassword"
                            value={formData.confirmPassword}
                            onChange={handleInputChange}
                            className="w-full p-2 rounded border border-gray-300 text-black"
                            placeholder="Confirm your new password"
                            required
                            disabled={isLoading}
                            autoComplete="new-password"
                        />
                        {errors.confirmPassword && (
                            <div className="text-red-500 text-sm mt-1">{errors.confirmPassword}</div>
                        )}
                    </div>

                    <button 
                        type="submit"
                        className={`w-full p-2 bg-lime-400 text-black rounded hover:bg-lime-500 transition-colors ${
                            isLoading ? 'opacity-50 cursor-not-allowed' : ''
                        }`}
                        disabled={isLoading}
                    >
                        {isLoading ? 'Resetting...' : 'Reset Password'}
                    </button>
                </form>

                <p className="mt-4 text-center">
                    <Link to="/login" className="text-lime-400 hover:underline">Back to Login</Link>
                </p>
            </div>
        </div>
    );
};

export default ResetPassword;

